import { useTranslation } from 'react-i18next'
import type { InjectionSpec, InjectionType, SetGenerationResult, TdLine } from '../types/testdata.types'
import { INJECTION_TYPES } from '../hooks/useErrorInjection'

const inputCls = 'rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors'

const LINE_BASED: InjectionType[] = ['price_deviation', 'missing_invoice_line', 'partial_delivery', 'overbilling', 'wrong_vat_rate']

interface Props {
  enabled: boolean
  onToggle: (value: boolean) => void
  injections: InjectionSpec[]
  onAdd: (type: InjectionType) => void
  onRemove: (idx: number) => void
  onUpdate: (idx: number, patch: Partial<InjectionSpec>) => void
  lines: TdLine[]
  result: SetGenerationResult | null
  onClearResult: () => void
}

function InjectionRow({ inj, idx, lines, onRemove, onUpdate }: {
  inj: InjectionSpec
  idx: number
  lines: TdLine[]
  onRemove: (idx: number) => void
  onUpdate: (idx: number, patch: Partial<InjectionSpec>) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40 rounded-lg px-3 py-2">
      <span className="text-sm font-medium text-amber-900 dark:text-amber-200 min-w-[180px]">
        {t(`error_injection.types.${inj.injection_type}`)}
      </span>
      {LINE_BASED.includes(inj.injection_type) && (
        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          {t('error_injection.param_line')}
          <select
            className={inputCls}
            value={inj.line_index}
            onChange={e => onUpdate(idx, { line_index: Number(e.target.value) })}
          >
            {lines.map((ln, i) => (
              <option key={i} value={i}>{ln.article_number || t('error_injection.line_n', { n: i + 1 })}</option>
            ))}
          </select>
        </label>
      )}
      {inj.injection_type === 'price_deviation' && (
        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          {t('error_injection.param_price_delta')}
          <input
            type="number" step="1" className={`${inputCls} w-20`}
            value={inj.price_delta_pct}
            onChange={e => onUpdate(idx, { price_delta_pct: Number(e.target.value) })}
          />%
        </label>
      )}
      {(inj.injection_type === 'partial_delivery' || inj.injection_type === 'overbilling') && (
        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          {t('error_injection.param_qty_delta')}
          <input
            type="number" step="1" min="1" className={`${inputCls} w-20`}
            value={inj.qty_delta}
            onChange={e => onUpdate(idx, { qty_delta: Number(e.target.value) })}
          />
        </label>
      )}
      {inj.injection_type === 'wrong_vat_rate' && (
        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          {t('error_injection.param_vat_rate')}
          <input
            type="number" step="0.1" className={`${inputCls} w-20`}
            placeholder={t('error_injection.param_auto')}
            value={inj.vat_rate ?? ''}
            onChange={e => onUpdate(idx, { vat_rate: e.target.value === '' ? null : Number(e.target.value) })}
          />%
        </label>
      )}
      {inj.injection_type === 'wrong_gln' && (
        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          {t('error_injection.param_wrong_gln')}
          <input
            className={`${inputCls} w-40`}
            placeholder={t('error_injection.param_auto')}
            value={inj.wrong_gln ?? ''}
            onChange={e => onUpdate(idx, { wrong_gln: e.target.value || null })}
          />
        </label>
      )}
      <span className="flex-1 text-xs text-gray-500 dark:text-gray-400">
        → {t(`error_injection.expected.${inj.injection_type}`)}
      </span>
      <button
        onClick={() => onRemove(idx)}
        className="text-gray-400 hover:text-red-500 text-sm leading-none transition-colors"
        title={t('common.delete')}
      >
        ×
      </button>
    </div>
  )
}

function VerificationResult({ result, onClear }: { result: SetGenerationResult; onClear: () => void }) {
  const { t } = useTranslation()
  const v = result.verification
  return (
    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {t('error_injection.result.title')}
        </h4>
        <button onClick={onClear} className="text-gray-400 hover:text-gray-600 text-sm">×</button>
      </div>
      <div className={`rounded-lg px-3 py-2 mb-3 text-sm font-medium ${
        v.all_detected
          ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
          : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
      }`}>
        {v.all_detected
          ? t('error_injection.result.all_detected', { count: v.outcomes.length })
          : t('error_injection.result.some_missed', {
              detected: v.outcomes.filter(o => o.detected).length,
              total: v.outcomes.length,
            })}
      </div>
      {v.outcomes.length > 0 && (
        <div className="space-y-1.5">
          {v.outcomes.map((o, i) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              <span className={o.detected ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                {o.detected ? '✓' : '✗'}
              </span>
              <div className="flex-1">
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {t(`error_injection.types.${o.injection_type}`)}
                </span>
                {o.article_number && <span className="text-gray-500 dark:text-gray-400"> — {o.article_number}</span>}
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('error_injection.result.expected')}: <code>{o.expected_status}</code>
                  {' · '}
                  {t('error_injection.result.actual')}: <code>{o.actual_status ?? '—'}</code>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {v.unexpected_findings.length > 0 && (
        <div className="mt-3">
          <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {t('error_injection.result.unexpected_title')}
          </h5>
          {v.unexpected_findings.map((finding, i) => (
            <div key={i} className="text-xs text-gray-500 dark:text-gray-400">
              {finding.article_number}: <code>{finding.status}</code> — {finding.details}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function ErrorInjectionPanel({ enabled, onToggle, injections, onAdd, onRemove, onUpdate, lines, result, onClearResult }: Props) {
  const { t } = useTranslation()
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={enabled}
          onChange={e => onToggle(e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('error_injection.toggle_label')}</span>
      </label>
      {enabled && (
        <>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t('error_injection.toggle_hint')}</p>
          <div className="mt-4 space-y-2">
            {injections.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">{t('error_injection.no_injections_hint')}</p>
            )}
            {injections.map((inj, idx) => (
              <InjectionRow key={idx} inj={inj} idx={idx} lines={lines} onRemove={onRemove} onUpdate={onUpdate} />
            ))}
          </div>
          <div className="mt-3">
            <select
              className={inputCls}
              value=""
              onChange={e => { if (e.target.value) onAdd(e.target.value as InjectionType); e.target.value = '' }}
            >
              <option value="" disabled>{t('error_injection.add_injection')}</option>
              {INJECTION_TYPES.map(type => (
                <option key={type} value={type}>{t(`error_injection.types.${type}`)}</option>
              ))}
            </select>
          </div>
          {result && <VerificationResult result={result} onClear={onClearResult} />}
        </>
      )}
    </div>
  )
}
